import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import { ShieldCheck } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { ActionCard, ResultOk, ResultError } from "@/components/admin/ActionCard";
import DeleteCoinCard from "@/components/admin/DeleteCoinCard";

/**
 * Non-technical admin page for one-click operations that would otherwise
 * require curl / terminals / hoppscotch.
 * The admin key is NEVER stored anywhere — held only in local component state.
 */
export default function Admin() {
  const [adminKey, setAdminKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, action, url, ... } | { error }
  const [discordBusy, setDiscordBusy] = useState(false);
  const [discordResult, setDiscordResult] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);
  const [tgResult, setTgResult] = useState(null);
  const [mailBusy, setMailBusy] = useState(false);
  const [mailResult, setMailResult] = useState(null);

  const [welcomeBusy, setWelcomeBusy] = useState(false);
  const [welcomeResult, setWelcomeResult] = useState(null);
  const [lifecycle, setLifecycle] = useState({});

  const runTestKind = (kind) => async () => {
    setLifecycle((l) => ({ ...l, [kind]: { busy: true } }));
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/test-email?kind=${kind}`, {}, { withCredentials: true });
      setLifecycle((l) => ({ ...l, [kind]: data }));
      toast.success(`${kind} email sent to ${data.sent_to}`);
    } catch (e) {
      const friendly = e?.response?.data?.detail || e?.message || "Unknown error";
      setLifecycle((l) => ({ ...l, [kind]: { error: friendly, status: e?.response?.status } }));
      toast.error(friendly);
    }
  };

  const runTestWelcome = async () => {
    setWelcomeBusy(true);
    setWelcomeResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/test-email?kind=welcome`, {}, { withCredentials: true });
      setWelcomeResult(data);
      toast.success(`Welcome email sent to ${data.sent_to}`);
    } catch (e) {
      const friendly = e?.response?.data?.detail || e?.message || "Unknown error";
      setWelcomeResult({ error: friendly, status: e?.response?.status });
      toast.error(friendly);
    } finally {
      setWelcomeBusy(false);
    }
  };

  const runTestEmail = async () => {
    setMailBusy(true);
    setMailResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/test-email`, {}, { withCredentials: true });
      setMailResult(data);
      toast.success(`Test alert sent to ${data.sent_to}`);
    } catch (e) {
      const friendly = e?.response?.data?.detail || e?.message || "Unknown error";
      setMailResult({ error: friendly, status: e?.response?.status });
      toast.error(friendly);
    } finally {
      setMailBusy(false);
    }
  };

  const runTestTelegram = async () => {
    setTgBusy(true);
    setTgResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/test-telegram`, {});
      setTgResult(data);
      toast.success("Test post sent — check your Telegram channel");
    } catch (e) {
      const friendly = e?.response?.data?.detail || e?.message || "Unknown error";
      setTgResult({ error: friendly, status: e?.response?.status });
      toast.error(friendly);
    } finally {
      setTgBusy(false);
    }
  };

  const runTestDiscord = async () => {
    setDiscordBusy(true);
    setDiscordResult(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/test-discord`, {});
      setDiscordResult(data);
      toast.success("Test ping sent — check your Discord channel");
    } catch (e) {
      const friendly = e?.response?.data?.detail || e?.message || "Unknown error";
      setDiscordResult({ error: friendly, status: e?.response?.status });
      toast.error(friendly);
    } finally {
      setDiscordBusy(false);
    }
  };

  const runSeedSpurr = async () => {
    setBusy(true);
    setResult(null);
    try {
      const headers = adminKey.trim() ? { "X-Admin-Key": adminKey.trim() } : {};
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/seed-spurr`, {}, { headers });
      setResult(data);
      toast.success(`$SPURR ${data.action} — visit /c/spurr`);
    } catch (e) {
      const status = e?.response?.status;
      const friendly = e?.response?.data?.detail || e?.message || "Unknown error";
      setResult({ error: friendly, status });
      toast.error(friendly);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="mb-10">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#cfff04] mb-3">
            // solspur admin console
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mb-3" data-testid="admin-title">
            Admin
          </h1>
          <p className="text-[#e2e2e2] max-w-xl">
            One-click operations. The seed action is safe to run without a key —
            all data it writes is hardcoded server-side.
          </p>
        </div>

        {/* Admin key input — held only in memory */}
        <div className="border border-[#3a3a3a] bg-[#121212] p-5 mb-6" data-testid="admin-key-block">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={14} className="text-[#cfff04]" />
            <label className="font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2]">
              Admin key (optional — not needed for seeding)
            </label>
          </div>
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Optional — leave empty for seeding"
            data-testid="admin-key-input"
            autoComplete="off"
            className="w-full bg-[#0c0c0c] border border-[#3a3a3a] focus:border-[#cfff04] focus:outline-none px-3 py-2 font-mono text-sm placeholder:text-[#d0d0d0]"
          />
          <p className="mt-2 font-mono text-[11px] text-[#d0d0d0]">
            // never stored — cleared on tab close · never logged
          </p>
        </div>

        <div className="space-y-4">
          <ActionCard
            title="Seed $SPURR genesis"
            description={
              <>
                Upserts the canonical <span className="text-[#cfff04] font-mono">$SPURR</span>{" "}
                coin — 1B supply, 40/25/15/10/7/3 tokenomics, full whitepaper, 4-phase roadmap,
                featured for 1 year.
              </>
            }
            busy={busy}
            busyLabel="Seeding…"
            idleLabel="Run"
            onRun={runSeedSpurr}
            runTestId="run-seed-spurr"
            cardTestId="action-seed-spurr"
          >
            {result?.ok && (
              <ResultOk label={`✓ SUCCESS — ${result.action}`} testid="seed-result-ok">
                <div className="text-sm text-[#e2e2e2] mb-2">
                  Tokenomics bullets: <span className="text-white">{result.tokenomics_bullets}</span>
                </div>
                <a
                  href={result.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="seed-result-link"
                  className="font-mono text-[11px] text-[#cfff04] underline hover:no-underline"
                >
                  Open {result.url} →
                </a>
              </ResultOk>
            )}
            {result?.error && <ResultError result={result} testid="seed-result-err" />}
          </ActionCard>

          <ActionCard
            title="Test Discord ping"
            description={
              <>
                Sends a sample &ldquo;new coin launched&rdquo; announcement to your Discord channel.
                Requires <span className="text-[#cfff04] font-mono">DISCORD_WEBHOOK_URL</span> in
                your deployment environment. Real announcements fire automatically on every paid
                publish &amp; feature.
              </>
            }
            busy={discordBusy}
            busyLabel="Sending…"
            idleLabel="Send test"
            onRun={runTestDiscord}
            runTestId="run-test-discord"
            cardTestId="action-test-discord"
          >
            {discordResult?.ok && (
              <ResultOk label="✓ SENT" testid="discord-result-ok">
                <div className="text-sm text-[#e2e2e2]">{discordResult.next_step}</div>
              </ResultOk>
            )}
            {discordResult?.error && <ResultError result={discordResult} testid="discord-result-err" />}
          </ActionCard>

          <ActionCard
            title="Test Telegram post"
            description={
              <>
                Posts the $SPURR share card + caption to your public Telegram channel. Requires{" "}
                <span className="text-[#cfff04] font-mono">TELEGRAM_BOT_TOKEN</span> and{" "}
                <span className="text-[#cfff04] font-mono">TELEGRAM_CHANNEL</span> (e.g. @solspur_launches)
                in your deployment environment. Every paid publish &amp; feature auto-posts. Public feeds:{" "}
                <a href={`${BACKEND_URL}/api/feed/launches.json`} className="text-[#cfff04] underline" target="_blank" rel="noreferrer" data-testid="feed-json-link">JSON</a>
                {" · "}
                <a href={`${BACKEND_URL}/api/feed/launches.rss`} className="text-[#cfff04] underline" target="_blank" rel="noreferrer" data-testid="feed-rss-link">RSS</a>
              </>
            }
            busy={tgBusy}
            busyLabel="Sending…"
            idleLabel="Send test"
            onRun={runTestTelegram}
            runTestId="run-test-telegram"
            cardTestId="action-test-telegram"
          >
            {tgResult?.ok && (
              <ResultOk label="✓ SENT" testid="telegram-result-ok">
                <div className="text-sm text-[#e2e2e2]">{tgResult.next_step}</div>
              </ResultOk>
            )}
            {tgResult?.error && <ResultError result={tgResult} testid="telegram-result-err" />}
          </ActionCard>

          <ActionCard
            title="Test email alert"
            description={
              <>
                Sends a sample watcher alert (Resend, from <span className="text-[#cfff04] font-mono">alerts@solspur.com</span>)
                to the email of the account you're signed in with. Signed-in watchers get these for whale tips, mints, pool launches and verification.
              </>
            }
            busy={mailBusy}
            busyLabel="Sending…"
            idleLabel="Send test email"
            onRun={runTestEmail}
            runTestId="run-test-email"
            cardTestId="action-test-email"
          >
            {mailResult?.ok && (
              <ResultOk label="✓ SENT" testid="email-result-ok">
                <div className="text-sm text-[#e2e2e2]">{mailResult.next_step}</div>
              </ResultOk>
            )}
            {mailResult?.error && <ResultError result={mailResult} testid="email-result-err" />}
          </ActionCard>

          <ActionCard
            title="Test welcome email"
            description={
              <>
                Every new signup (email or Google) automatically gets a "your first coin in 3 steps" welcome from{" "}
                <span className="text-[#cfff04] font-mono">alerts@solspur.com</span>, once per account. This sends you a copy.
              </>
            }
            busy={welcomeBusy}
            busyLabel="Sending…"
            idleLabel="Send me the welcome"
            onRun={runTestWelcome}
            runTestId="run-test-welcome"
            cardTestId="action-test-welcome"
          >
            {welcomeResult?.ok && (
              <ResultOk label="✓ SENT" testid="welcome-result-ok">
                <div className="text-sm text-[#e2e2e2]">{welcomeResult.next_step}</div>
              </ResultOk>
            )}
            {welcomeResult?.error && <ResultError result={welcomeResult} testid="welcome-result-err" />}
          </ActionCard>

          <ActionCard
            title="Test 48h launch nudge"
            description="Signups who haven't published after 48 hours get one email with a one-tap link back to their saved draft (or to /create). Sent once per account, hourly job, respects unsubscribe."
            busy={!!lifecycle.nudge?.busy}
            busyLabel="Sending…"
            idleLabel="Send me the nudge"
            onRun={runTestKind("nudge")}
            runTestId="run-test-nudge"
            cardTestId="action-test-nudge"
          >
            {lifecycle.nudge?.ok && <ResultOk label="✓ SENT" testid="nudge-result-ok"><div className="text-sm text-[#e2e2e2]">{lifecycle.nudge.next_step}</div></ResultOk>}
            {lifecycle.nudge?.error && <ResultError result={lifecycle.nudge} testid="nudge-result-err" />}
          </ActionCard>

          <ActionCard
            title="Test weekly creator digest"
            description="Every Monday, creators with a published coin get last week's views, tips, watchers and holder growth per coin (vs the week before). Sends you yours now — requires you own a published coin."
            busy={!!lifecycle.digest?.busy}
            busyLabel="Sending…"
            idleLabel="Send me my digest"
            onRun={runTestKind("digest")}
            runTestId="run-test-digest"
            cardTestId="action-test-digest"
          >
            {lifecycle.digest?.ok && <ResultOk label="✓ SENT" testid="digest-result-ok"><div className="text-sm text-[#e2e2e2]">{lifecycle.digest.next_step}</div></ResultOk>}
            {lifecycle.digest?.error && <ResultError result={lifecycle.digest} testid="digest-result-err" />}
          </ActionCard>
        </div>

        <div className="border border-[#3a3a3a] bg-[#121212] p-5 mt-4" data-testid="action-mint-spurr">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-display font-bold text-lg mb-1">Mint $SPURR on-chain</h2>
              <p className="text-sm text-[#e2e2e2] max-w-md">
                The genesis coin page now has a one-click mint panel — connect Phantom, do a free
                devnet practice run, then mint the real 1B <span className="text-[#cfff04] font-mono">$SPURR</span>{" "}
                on mainnet. Mint + freeze authority are revoked automatically (rug-safe).
              </p>
            </div>
            <a
              href="/c/spurr#mint"
              data-testid="mint-spurr-link"
              className="shrink-0 border border-[#cfff04] text-[#cfff04] hover:bg-[#cfff04] hover:text-[#050505] px-5 py-2 font-mono text-xs uppercase tracking-widest transition-colors"
            >
              Open mint panel →
            </a>
          </div>
        </div>

        <DeleteCoinCard adminKey={adminKey} />

        <div className="mt-10 font-mono text-[11px] text-[#d0d0d0] uppercase tracking-widest">
          // solspur admin · v0.1
        </div>
      </main>

      <Footer />
    </div>
  );
}
